import dayjs, { DATE_FORMAT } from '@/libs/dayjs'
import i18n from '@/libs/i18n'
import {
    ContractDuration,
    EngagementBasis,
    StaffAcknowledgement,
    StaffCoverageReason,
    StaffNotificationState,
    StaffPayrollFormat,
    StaffPayrollHours,
    StaffPayrollProvider,
    StaffShiftState,
    TimeRecordBasis,
    TimeRecordExemption,
    WorkingTimeType,
} from '@/enums/staff'
import { useFetchStore } from '@/stores/fetch'
import type { SelectOption, TagItem } from '@/types/Common'
import type { IStaffPayroll } from '@/types/Property'
import type {
    IStaffEmployment,
    IStaffEmploymentRequest,
    IStaffShiftChange,
    IStaffWorkPattern,
} from '@/types/Staff'

/**
 * Shared wording and arithmetic for the staff screens (docs/OSEBJE.md).
 *
 * Every label here is a literal i18n key rather than one assembled from a wire value,
 * so that `yarn check-i18n` can see it. A value the API knows and this table does not
 * is shown as itself — visibly untranslated rather than invisibly blank.
 */

/** The zero ObjectID. The API spells "no department" and "the whole house" with it. */
export const NO_ID = '000000000000000000000000'

/**
 * The house's rota rules (docs/OSEBJE.md §7, „Pravila urnika“): the statutory defaults a
 * house gets for saying nothing, and the bounds the API refuses past.
 *
 * They are here rather than inline in the property form because the same numbers are the
 * form's placeholders AND its validation, and a ceiling typed twice is a ceiling that will
 * one day differ from the server's. The DEFAULTS are shown as placeholders and never
 * written into a new property: zero is how "leave it at whatever the law says" is stored,
 * and a form that pre-filled 660 would make the statutory floor look like a figure this
 * hotel chose.
 *
 * Nothing here computes a rule. The rules are the server's, in Go, and the portal draws
 * the sentences it answers with — see IStaffShiftWarning.
 */
export const STAFF_RULES_DEFAULT_DAILY_REST = 660

export const STAFF_RULES_DEFAULT_WEEKLY_REST = 1440

export const STAFF_RULES_DEFAULT_CONSECUTIVE_DAYS = 6

export const STAFF_RULES_DEFAULT_MAX_DAILY = 600

export const STAFF_RULES_DEFAULT_WEEKLY_OVERTIME = 480

/** Three days between two shifts is a typing error with a nought in it. */
export const STAFF_RULES_MAX_REST_MINUTES = 4320

/** A fortnight without a day off is already the rule switched off by another name. */
export const STAFF_RULES_MAX_CONSECUTIVE_DAYS = 14

/** More than a full week over the contract is a second obligation, not a tolerance. */
export const STAFF_RULES_MAX_WEEKLY_OVERTIME = 10080

/**
 * How long a shift may run with nobody stamped in before „Danes“ asks about it
 * (docs/OSEBJE.md §11 „Danes“). Fifteen minutes, and it decides only whether somebody is
 * asked — the record itself is untouched, exactly as the excess threshold is (§8).
 */
export const STAFF_RULES_DEFAULT_LATE_STAMP = 15

/** Half a day. Longer than that is the section switched off under another name. */
export const STAFF_RULES_MAX_LATE_STAMP = 720

export const MINUTES_PER_DAY = 1440

/**
 * What a payroll handle may be as long as — `DataStructures.StaffPayrollCompanyCodeMaxLength`
 * and its two siblings, all forty.
 *
 * Checked in the form as well as on the server for the reason every other bound here is:
 * a person who has just typed a code is owed the refusal before a round trip. Forty
 * because these are somebody else's identifiers and we neither shape them nor shorten
 * them — a handle, not a sentence.
 */
export const STAFF_PAYROLL_CODE_MAX = 40

/**
 * The empty payroll block, as the property form holds it before a house has answered
 * anything (docs/EVIDENCA-CASA.md § „Payroll Connect“).
 *
 * The three codebook fields carry their DEFAULTS rather than empty strings, unlike the
 * rota rules above — and the difference is what a zero MEANS in each. There, nought is how
 * "leave it at whatever the law says" is stored, so pre-filling it would make the statutory
 * floor look like a figure this hotel chose. Here, an empty provider is not a state a house
 * can be in: the server resolves it to `generic`, and a select showing a blank option would
 * offer a value no codebook contains.
 *
 * `absence_codes` is empty and stays empty until an accountant fills it in — see
 * IStaffPayroll.
 */
export const emptyStaffPayroll = (): IStaffPayroll => ({
    provider:      StaffPayrollProvider.Generic,
    company_code:  '',
    export_format: StaffPayrollFormat.Csv,
    decimal_comma: true,
    hours_as:      StaffPayrollHours.Decimal,
    absence_codes: {},
})

/** The payroll programs a house may pick, in the order the settings section offers them. */
export const staffPayrollProviderOptions = (): SelectOption[] => [
    { label: i18n.global.t('staff_payroll.provider_generic'),  value: StaffPayrollProvider.Generic },
    { label: i18n.global.t('staff_payroll.provider_vasco'),    value: StaffPayrollProvider.Vasco },
    { label: i18n.global.t('staff_payroll.provider_saop'),     value: StaffPayrollProvider.Saop },
    { label: i18n.global.t('staff_payroll.provider_pantheon'), value: StaffPayrollProvider.Pantheon },
    { label: i18n.global.t('staff_payroll.provider_minimax'),  value: StaffPayrollProvider.Minimax },
]

/**
 * The file formats, with the one this service cannot write LABELLED as such.
 *
 * Offered rather than hidden, because a house genuinely on a program that wants a workbook
 * should be able to say so — and then be told, once, on the settings screen, instead of at
 * the download button every month. The label carries the sentence; nothing here refuses.
 */
export const staffPayrollFormatOptions = (): SelectOption[] => [
    { label: i18n.global.t('staff_payroll.format_csv'),  value: StaffPayrollFormat.Csv },
    { label: i18n.global.t('staff_payroll.format_xlsx'), value: StaffPayrollFormat.Xlsx },
]

/** How hours are spelled in the file — a spelling and never a rounding. */
export const staffPayrollHoursOptions = (): SelectOption[] => [
    { label: i18n.global.t('staff_payroll.hours_decimal'), value: StaffPayrollHours.Decimal },
    { label: i18n.global.t('staff_payroll.hours_hhmm'),    value: StaffPayrollHours.HHMM },
]

export const isNoId = (id?: string | null): boolean => !id || id === NO_ID

/**
 * Minutes as hours and minutes: `450` → `7 h 30 min`.
 *
 * Minutes are the stored unit and hours are only a way of printing them (§8, and the
 * house rule that nothing in a measured path is a float). A whole number of hours drops
 * the minutes, because "8 h 0 min" is a column nobody reads twice.
 */
export const formatMinutes = (minutes?: number | null): string => {
    const total = Math.trunc(minutes ?? 0)
    const sign  = total < 0 ? '−' : ''
    const value = Math.abs(total)

    const hours = Math.floor(value / 60)
    const rest  = value % 60

    if (!hours) return `${sign}${rest} ${i18n.global.t('staff.unit_minutes')}`
    if (!rest)  return `${sign}${hours} ${i18n.global.t('staff.unit_hours')}`

    return `${sign}${hours} ${i18n.global.t('staff.unit_hours')} ${rest} ${i18n.global.t('staff.unit_minutes')}`
}

/** The Monday of the week a date falls in, as `YYYY-MM-DD`. */
export const weekStart = (date: string): string => {
    const day = dayjs(date)

    // dayjs counts Sunday as 0; a hotel week starts on Monday, so Sunday is six days
    // after the Monday it belongs to rather than one before the next one.
    const offset = (day.day() + 6) % 7

    return day.subtract(offset, 'day').format(DATE_FORMAT)
}

/** The seven days of a week beginning on `start`, as `YYYY-MM-DD`. */
export const weekDays = (start: string): string[] =>
    Array.from({ length: 7 }, (_, index) => dayjs(start).add(index, 'day').format(DATE_FORMAT))

/**
 * A name for a retry that survives it (CLAUDE.md invariant 5).
 *
 * Publishing and copying a week both carry one: the button gets pressed again when the
 * answer seems to hang, and the second press must publish the same week once rather
 * than announce it twice.
 */
export const newOperationId = (): string => crypto.randomUUID()

/** The tag for a shift's state. Draft is not a failure — it is a week not yet promised. */
export const getShiftState = (state: string): TagItem => {
    const map: Record<string, TagItem> = {
        [StaffShiftState.Draft]:     { label: i18n.global.t('staff.state_draft'),     color: 'warning' },
        [StaffShiftState.Published]: { label: i18n.global.t('staff.state_published'), color: 'success' },
        [StaffShiftState.Cancelled]: { label: i18n.global.t('staff.state_cancelled'), color: 'error' },
    }

    return map[state] ?? { label: state, color: 'neutral' }
}

/**
 * Go's zero `time.Time` on the wire. A withdrawal is a change with a zero NEW interval,
 * and a change nobody has acknowledged has a zero `notified_at` — both arrive as this,
 * not as null.
 */
export const isZeroTime = (value?: string | null): boolean =>
    !value || value.startsWith('0001-01-01')

/** A change with no new interval took the shift away rather than moving it (§12). */
export const shiftChangeWithdrawn = (change: IStaffShiftChange): boolean =>
    isZeroTime(change.new_starts_at) && isZeroTime(change.new_ends_at)

/**
 * One interval, written the way somebody reads a shift: `31. 8. 06:00–14:00`.
 *
 * A shift that runs past midnight names the second date as well, because "22:00–06:00"
 * with one date on it is the sentence people misread — and misreading a shift is the
 * whole failure this trail exists to prevent.
 */
export const shiftInterval = (from?: string | null, to?: string | null): string => {
    if (!from || !to) return ''

    const start = dayjs(from)
    const end   = dayjs(to)

    if (start.format(DATE_FORMAT) === end.format(DATE_FORMAT)) {
        return `${start.format('D. M. HH:mm')}–${end.format('HH:mm')}`
    }

    return `${start.format('D. M. HH:mm')} – ${end.format('D. M. HH:mm')}`
}

/**
 * A change as one line: FROM WHAT TO WHAT.
 *
 * docs/OSEBJE.md §7 is exact about why this sentence and not "your shift changed": a
 * person has arranged their week around what they read, and the only useful thing to
 * tell them is the difference. A withdrawal reads differently because it is a different
 * event — "your Saturday moved" and "your Saturday is gone" must never look alike.
 */
export const shiftChangeLine = (change: IStaffShiftChange): string => {
    const previous = shiftInterval(change.previous_starts_at, change.previous_ends_at)

    if (shiftChangeWithdrawn(change)) {
        return i18n.global.t('staff.change_withdrawn', { previous })
    }

    return i18n.global.t('staff.change_moved', {
        previous,
        current: shiftInterval(change.new_starts_at, change.new_ends_at),
    })
}

/**
 * The same change said about SOMEBODY: „Ivana Sobarica: turnus je odpovedan (bil je 31.
 * 8. 07:00–15:00)“.
 *
 * The name comes first because it is the first thing a supervisor needs — the entry
 * exists so that she knows whose door to knock on, and a sentence that gets to the
 * person last is one she reads twice. A change whose account the service could not
 * resolve falls back to the bare line rather than to a colon with nothing before it.
 *
 * # Why the sentence is lower-cased
 *
 * Because after a name and a colon it is no longer the start of one, and both languages
 * this portal speaks write „turnus“ and "the shift" in lower case mid sentence. It is
 * done here rather than by a second pair of locale strings so that the sentence has
 * exactly ONE spelling: two would be two places to fix the day the wording changes, and
 * the second would be the one nobody found.
 */
export const shiftChangeForPerson = (change: IStaffShiftChange): string => {
    const line = shiftChangeLine(change)

    if (!change.user_name) return line

    return i18n.global.t('staff.change_for_person', {
        name:   change.user_name,
        change: line.charAt(0).toLocaleLowerCase() + line.slice(1),
    })
}

/**
 * The tag for how far a notice got.
 *
 * Only `delivered` is green, and that is the point: everything else — waiting, sent,
 * failed — is one fact wearing three explanations, namely that this person has not been
 * told. A screen that drew "sent" as a success would be the screen version of the
 * mistake §7 was written to stop.
 */
/**
 * The tag for a change on a supervisor's list: what it is WAITING for.
 *
 * A decline is not a delivery state and must not be drawn as one. „Poslano,
 * nepotrjeno“ beside somebody who has answered „Ne morem“ is true about the transport
 * and wrong about the morning — it says nobody knows, when in fact one person knows and
 * cannot come. So the decline wins, in red, and the delivery state is what everything
 * else is drawn from.
 */
export const shiftChangeTag = (change: IStaffShiftChange): TagItem =>
    shiftChangeDeclined(change)
        ? { label: i18n.global.t('staff.notice_declined'), color: 'error' }
        : notificationStateTag(change.notification_state)

export const notificationStateTag = (state: string): TagItem => {
    const map: Record<string, TagItem> = {
        [StaffNotificationState.Pending]:   { label: i18n.global.t('staff.notice_pending'),   color: 'warning' },
        [StaffNotificationState.Sent]:      { label: i18n.global.t('staff.notice_sent'),      color: 'warning' },
        [StaffNotificationState.Delivered]: { label: i18n.global.t('staff.notice_delivered'), color: 'success' },
        [StaffNotificationState.Failed]:    { label: i18n.global.t('staff.notice_failed'),    color: 'error' },
    }

    return map[state] ?? { label: state, color: 'neutral' }
}

/**
 * Whether the person answered „Ne morem“ (docs/OBVESCANJE.md §10.1).
 *
 * It is read off `acknowledged_how` and not off `declined_at`, so that the client and
 * the service agree by one field: the API's own `Declined()` is the same comparison, and
 * a screen deciding from a timestamp would disagree with it the day a decline is undone.
 */
export const shiftChangeDeclined = (change: IStaffShiftChange): boolean =>
    change.acknowledged_how === StaffAcknowledgement.Declined

/**
 * A decline said about somebody: „Ivana Sobarica je zavrnila spremembo turnusa: ne
 * morem, bolna“.
 *
 * The name first, for the reason `shiftChangeForPerson` puts it first: the row exists so
 * that a supervisor knows whose Saturday she is covering, and a sentence that gets to the
 * person last is one she reads twice. A change whose account the service could not
 * resolve falls back to the sentence without a name rather than to a colon with nothing
 * before it.
 *
 * The note is the person's own words and is often absent — a button carries none — so
 * there are two sentences and not one with an empty tail. „…: “ with nothing after it is
 * the shape that makes a screen look broken.
 */
export const shiftChangeDeclineLine = (change: IStaffShiftChange): string => {
    const note = (change.decline_note ?? '').trim()
    const name = change.user_name

    if (!name) {
        return note
            ? i18n.global.t('staff.declined_line_noted', { note })
            : i18n.global.t('staff.declined_line')
    }

    return note
        ? i18n.global.t('staff.declined_by_noted', { name, note })
        : i18n.global.t('staff.declined_by', { name })
}

export const engagementBasisLabel = (value: string): string => {
    const map: Record<string, string> = {
        [EngagementBasis.EmploymentContract]:  i18n.global.t('staff.basis_employment_contract'),
        [EngagementBasis.AgencyWorker]:        i18n.global.t('staff.basis_agency_worker'),
        [EngagementBasis.StudentWork]:         i18n.global.t('staff.basis_student_work'),
        [EngagementBasis.PensionerTemporary]:  i18n.global.t('staff.basis_pensioner_temporary'),
        [EngagementBasis.Traineeship]:         i18n.global.t('staff.basis_traineeship'),
        [EngagementBasis.ServiceContract]:     i18n.global.t('staff.basis_service_contract'),
        [EngagementBasis.CopyrightContract]:   i18n.global.t('staff.basis_copyright_contract'),
        [EngagementBasis.ShortTermFamilyWork]: i18n.global.t('staff.basis_short_term_family_work'),
        [EngagementBasis.SelfEmployed]:        i18n.global.t('staff.basis_self_employed'),
        [EngagementBasis.Other]:               i18n.global.t('staff.basis_other'),
    }

    return map[value] ?? value
}

export const contractDurationLabel = (value: string): string => {
    const map: Record<string, string> = {
        [ContractDuration.Indefinite]: i18n.global.t('staff.duration_indefinite'),
        [ContractDuration.FixedTerm]:  i18n.global.t('staff.duration_fixed_term'),
    }

    return map[value] ?? value
}

export const workingTimeTypeLabel = (value: string): string => {
    const map: Record<string, string> = {
        [WorkingTimeType.FullTime]: i18n.global.t('staff.time_full'),
        [WorkingTimeType.PartTime]: i18n.global.t('staff.time_part'),
    }

    return map[value] ?? value
}

export const timeRecordBasisLabel = (value: string): string => {
    const map: Record<string, string> = {
        [TimeRecordBasis.Clock]:    i18n.global.t('staff.record_clock'),
        [TimeRecordBasis.Schedule]: i18n.global.t('staff.record_schedule'),
        [TimeRecordBasis.Pattern]:  i18n.global.t('staff.record_pattern'),
        [TimeRecordBasis.Manual]:   i18n.global.t('staff.record_manual'),
    }

    return map[value] ?? value
}

export const timeRecordExemptionLabel = (value: string): string => {
    const map: Record<string, string> = {
        [TimeRecordExemption.ManagingPerson]: i18n.global.t('staff.exempt_managing_person'),
        [TimeRecordExemption.Other]:          i18n.global.t('staff.exempt_other'),
    }

    return map[value] ?? value
}

/**
 * The name of a requirement source. The core contributes arrivals and departures and
 * has no module key at all, which is why the empty string is a real entry here.
 */
export const coverageSourceLabel = (module: string): string => {
    const map: Record<string, string> = {
        '':             i18n.global.t('staff.source_core'),
        housekeeping:   i18n.global.t('staff.source_housekeeping'),
        dining:         i18n.global.t('staff.source_dining'),
        events:         i18n.global.t('staff.source_events'),
        maintenance:    i18n.global.t('staff.source_maintenance'),
    }

    return map[module] ?? module
}

/**
 * WHY a source contributed nothing — and it is printed rather than folded away.
 *
 * A quiet nought is the single worst thing the coverage screen could show: it looks
 * exactly like a Tuesday with no departures (§11). The four sentences are also four
 * different conversations — "the house has not bought it" is a sales one and "we have
 * not built it" is ours — which is why they are four codes and not one.
 */
export const coverageReasonLabel = (reason: string): string => {
    const map: Record<string, string> = {
        [StaffCoverageReason.Contributing]: i18n.global.t('staff.reason_contributing'),
        [StaffCoverageReason.NotLicensed]:  i18n.global.t('staff.reason_not_licensed'),
        [StaffCoverageReason.NotBuilt]:     i18n.global.t('staff.reason_not_built'),
        [StaffCoverageReason.NoDepartment]: i18n.global.t('staff.reason_no_department'),
        [StaffCoverageReason.NoRule]:       i18n.global.t('staff.reason_no_rule'),
    }

    return map[reason] ?? reason
}

/** The colour a source's standing is drawn in. Only "contributing" is a measurement. */
export const coverageReasonTag = (reason: string): TagItem => ({
    label: coverageReasonLabel(reason),
    color: reason === StaffCoverageReason.Contributing ? 'success' : 'warning',
})

export const emptyWorkPattern = (): IStaffWorkPattern => ({
    monday:    0,
    tuesday:   0,
    wednesday: 0,
    thursday:  0,
    friday:    0,
    saturday:  0,
    sunday:    0,
})

/** The pattern's own weekly total, for the screen to compare against the obligation. */
export const workPatternMinutes = (pattern: IStaffWorkPattern): number =>
    pattern.monday + pattern.tuesday + pattern.wednesday + pattern.thursday
    + pattern.friday + pattern.saturday + pattern.sunday

/** The seven fields of a work pattern, in the order a week is read. */
export const workPatternDays = (): { key: keyof IStaffWorkPattern, label: string }[] => [
    { key: 'monday',    label: i18n.global.t('staff.day_monday') },
    { key: 'tuesday',   label: i18n.global.t('staff.day_tuesday') },
    { key: 'wednesday', label: i18n.global.t('staff.day_wednesday') },
    { key: 'thursday',  label: i18n.global.t('staff.day_thursday') },
    { key: 'friday',    label: i18n.global.t('staff.day_friday') },
    { key: 'saturday',  label: i18n.global.t('staff.day_saturday') },
    { key: 'sunday',    label: i18n.global.t('staff.day_sunday') },
]

/** An English weekday name from the API, in the language the screen is drawn in. */
export const weekdayLabel = (weekday: string): string => {
    const map: Record<string, string> = {
        Monday:    i18n.global.t('staff.day_monday'),
        Tuesday:   i18n.global.t('staff.day_tuesday'),
        Wednesday: i18n.global.t('staff.day_wednesday'),
        Thursday:  i18n.global.t('staff.day_thursday'),
        Friday:    i18n.global.t('staff.day_friday'),
        Saturday:  i18n.global.t('staff.day_saturday'),
        Sunday:    i18n.global.t('staff.day_sunday'),
    }

    return map[weekday] ?? weekday
}

/** Options for a select, built from a labelling function over a set of codes. */
export const optionsFrom = (values: string[], label: (value: string) => string): SelectOption[] =>
    values.map(value => ({ value, label: label(value) }))

/**
 * A Go zero time — year 1 — is how the API spells "open ended" on a period and "never"
 * on a moment. Printed as a date it reads as 1. 1. 1, which is nobody's answer.
 */
export const isOpenEnded = (value?: string | null): boolean =>
    !value || value.startsWith('0001-01-01')

/**
 * A date the service holds as a `time.Time`, as a form value: `YYYY-MM-DD`, and the empty
 * string where the service is holding its zero.
 *
 * Every dated field on the personnel card arrives as an instant and goes back as a plain
 * day (`DataStructures.ParseAPIDate` reads both), so the conversion belongs in one place —
 * a date box handed `0001-01-01T00:00:00Z` draws the first of January of the year one, and
 * a form that then saves it writes that back.
 */
export const toFormDate = (value?: string | null): string =>
    isOpenEnded(value) ? '' : dayjs(value).format(DATE_FORMAT)

/**
 * Minutes in, hours on the screen.
 *
 * The service stores an obligation in MINUTES, which is right — an integer that cannot
 * drift. Nobody types 2400 for a forty-hour week, though, so the two inputs that carry
 * an obligation work in hours and convert at the edge of the form. Quarter-hour steps
 * cover every real contract (40, 37.5, 20, 8.25) without ever needing a third decimal.
 *
 * The rounding is deliberate in both directions: hours are shown to two decimals so a
 * stored value that is not a whole quarter still reads honestly, and minutes are rounded
 * on the way back so the number written is always an integer.
 */
/**
 * A period as the API wants it BACK, built from the period it just answered.
 *
 * The update route takes the whole record, so a screen that edits four fields of a card
 * still has to send all of them. Written here rather than inside the leave tab that needs
 * it, because it is a mapping between two shapes of the same record — and the dialogue
 * that edits a period builds the same body field by field from a form. One of them will
 * gain a field one day; a named mapping is where the second one goes to find out.
 *
 * The id and the resolved name are dropped: both are answers, not things a caller sends.
 * The three lists are made non-null, because Go marshals an empty slice as `null` on the
 * way out and refuses one on the way in.
 */
export const employmentRequestFrom = (period: IStaffEmployment): IStaffEmploymentRequest => {
    // Copied and then stripped rather than destructured past: two named bindings nobody
    // reads are two lint errors, and `delete` on a shallow copy says the same thing.
    const rest = { ...period } as Partial<IStaffEmployment>

    delete rest.id
    delete rest.user_name

    return {
        ...(rest as Omit<IStaffEmployment, 'id' | 'user_name'>),
        work_property_ids:   period.work_property_ids ?? [],
        department_ids:      period.department_ids ?? [],
        work_department_ids: period.work_department_ids ?? [],
        children:            period.children ?? [],
    }
}

export const minutesToHours = (minutes?: number | null): number =>
    Math.round(((minutes ?? 0) / 60) * 100) / 100

export const hoursToMinutes = (hours?: number | null): number =>
    Math.round((hours ?? 0) * 60)

/**
 * „Sporočeno osebno“ — the supervisor recording that she told the person face to face.
 *
 * # Why a house needs a button for a conversation
 *
 * docs/OBVESCANJE.md §5 has three outcomes for a notice and two of them dead-end: one
 * that spent its retry budget and is `failed`, and a person who carries no channel at
 * all, whose change simply stays undelivered. In both, the delivery that actually
 * happens is somebody walking up to them at the handover — and until it could be written
 * down, the supervisor's list said "nobody has been told" about somebody she had
 * personally told, every day, for ever. A list with a permanent row in it is a list
 * people stop reading, which costs exactly the Saturday §7 was written to protect.
 *
 * It is deliberately NOT the same call as the person's own „Potrjujem“. That one comes
 * off the session with no house in it and writes `delivered`; this one names the house,
 * needs `staff:schedule` or `staff:oversee`, and leaves the delivery state alone — the
 * message really did fail, and a screen that painted it green would hide a broken
 * gateway behind a supervisor's diligence.
 *
 * Answers whether it took, so a caller can reload the list rather than guess.
 */
export const acknowledgeShiftChangeInPerson = async (propertyId: string, changeId: string): Promise<boolean> => {
    const { statusCode } = await useFetchStore()
        .fetch(`/properties/${propertyId}/staff/shift-changes/${changeId}/acknowledge`)
        .post()
        .json<IStaffShiftChange>()

    return statusCode.value === 200
}
